import type {
  LicenseResponseBody,
  ReactivateLicenseResponseBody,
} from '@/features/Licenses/api/licenseHooks'
import {
  invalidateOrgLicenses,
  setOrgLicenseQueryData,
  useReactivateOrgLicense,
} from '@/features/Licenses/api/licenseHooks'
import { LicenseFormDialog } from '@/features/Licenses/components/LicenseFormDialog'
import {
  ERROR_TOAST_OPTION,
  SUCCESS_TOAST_OPTION,
  showOrUpdateToast,
} from '@/hooks/useToast/toastUtils'
import { Button, Text } from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

type Props = { license: LicenseResponseBody; onSuccess: () => void }

export function ReactivateForm({ license, onSuccess }: Readonly<Props>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const queryClient = useQueryClient()

  const mutation = useReactivateOrgLicense({
    onSuccess: (data: ReactivateLicenseResponseBody) => {
      setOrgLicenseQueryData(queryClient, license.id, data)
      invalidateOrgLicenses(queryClient, license.orgId)
      showOrUpdateToast('reactivate-license-success', {
        ...SUCCESS_TOAST_OPTION,
        title: data.reactivated
          ? String(t('licenses.actions.reactivate.success.reactivated'))
          : String(t('licenses.actions.reactivate.success.alreadyActive')),
      })
      onSuccess()
    },
    onError: () => {
      showOrUpdateToast('reactivate-license-error', {
        ...ERROR_TOAST_OPTION,
        title: String(t('licenses.actions.reactivate.error.title')),
        description: String(t('licenses.actions.reactivate.error.generic')),
      })
    },
  })

  const handleSubmit = () => {
    mutation.mutate({
      pathParams: { orgId: license.orgId, licenseId: license.id },
    })
  }

  return (
    <LicenseFormDialog
      title={String(t('licenses.actions.reactivate.title'))}
      errorTitle={String(t('licenses.actions.reactivate.error.title'))}
      inDialogError={null}
      cancelLabel={String(t('licenses.actions.reactivate.cancel'))}
      submitButton={
        <Button
          colorPalette="brand"
          disabled={mutation.isPending}
          loading={mutation.isPending}
          onClick={handleSubmit}
        >
          {String(t('licenses.actions.reactivate.submitButton'))}
        </Button>
      }
    >
      <Text fontSize="sm">
        {String(t('licenses.actions.reactivate.warning'))}
      </Text>
    </LicenseFormDialog>
  )
}
